import React, { useCallback, useRef, useState } from 'react';
import ReactMapboxGl, { ScaleControl } from 'react-mapbox-gl';
import PropTypes from 'prop-types';
import useDimensions from 'react-cool-dimensions';
import i18n from 'i18next';
import useStyles from './Map.styles';
import { Button } from '../../common/components/buttons';

const attribution =
  '<a href="http://www.openmaptiles.org/" target="_blank">&copy; OpenMapTiles</a> <a href="http://www.openstreetmap.org/about/" target="_blank">&copy; OpenStreetMap contributors</a>';

const Map = ({ children, initialBounds, center, bearing, onStyleLoad, onManualMoveEnd, onManualZoomEnd, onAutomaticMoveEnd, onClick, mapClassName, zoom }) => {
  const classes = useStyles();
  const mapRef = useRef();

  const mapStyles = {
    satellite: 'mapbox://styles/mapbox/satellite-v9',
    streets: 'mapbox://styles/mapbox/streets-v9',
  };

  const { current: MapboxGL } = useRef(
    // IMPORTANT NOTE:
    // Since the component is created only once here, every event callback of the map cannot use the local state of the component.
    // This goes as well to every parent the callback is passed to.
    // For example, the props could not be used inside the callback of onMoveEnd.
    ReactMapboxGl({
      accessToken: process.env.REACT_APP_MAPBOX_ACCESS_TOKEN,
      minZoom: 3,
      maxZoom: 18,
      customAttribution: attribution,
      doubleClickZoom: false,
      pitchWithRotate: false,
      preserveDrawingBuffer: true,
    })
  );

  const { observe } = useDimensions({
    onResize({ height, width }) {
      if (mapRef.current) mapRef.current.resize();
      localStorage.setItem('mapDimensions', JSON.stringify({ height, width }));
    },
  });

  const [mapStyle, setMapStyle] = useState('streets');

  const handleStyleChange = useCallback(() => {
    setMapStyle((old) => (old === 'satellite' ? 'streets' : 'satellite'));
  }, []);

  const handleStyleLoad = useCallback(
    (map) => {
      mapRef.current = map;
      if (onStyleLoad) onStyleLoad(map);
    },
    [onStyleLoad]
  );

  const handleMoveEnd = useCallback(
    (map, event) => {
      if (onManualMoveEnd && event.originalEvent && !event.fitboundUpdate) {
        onManualMoveEnd(map, event);
      } else if (onAutomaticMoveEnd) {
        onAutomaticMoveEnd(map, event);
      }
    },
    [onManualMoveEnd, onAutomaticMoveEnd]
  );

  const handleZoomEnd = useCallback(
    (map, event) => {
      if (onManualZoomEnd && !event.fitboundUpdate) {
        onManualZoomEnd(map, event);
      }
    },
    [onManualZoomEnd]
  );

  return (
    <div id="map" ref={observe} className={classes.container}>
      <MapboxGL
        style={mapStyles[mapStyle]}
        className={`${classes.map} ${mapClassName}`}
        onStyleLoad={handleStyleLoad}
        onMoveEnd={handleMoveEnd}
        onZoomEnd={handleZoomEnd}
        fitBounds={initialBounds}
        fitBoundsOptions={{ padding: 100 }}
        onClick={onClick}
        center={center}
        zoom={zoom}
        bearing={bearing}
        movingMethod={center ? 'jumpTo' : 'flyTo'}
        renderChildrenInPortal>
        <Button id="styleButton" primary className={classes.styleButton} onClick={handleStyleChange}>
          {i18n.t('map.button.changeStyle')}
        </Button>

        <ScaleControl measurement="km" position="bottom-left" className={classes.scaleControl} />

        {children}
      </MapboxGL>
    </div>
  );
};

Map.propTypes = {
  bearing: PropTypes.arrayOf(PropTypes.number),
  center: PropTypes.arrayOf(PropTypes.number),
  children: PropTypes.node,
  initialBounds: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  mapClassName: PropTypes.string,
  onAutomaticMoveEnd: PropTypes.func,
  onClick: PropTypes.func,
  onManualMoveEnd: PropTypes.func,
  onManualZoomEnd: PropTypes.func,
  onStyleLoad: PropTypes.func,
  zoom: PropTypes.arrayOf(PropTypes.number).isRequired,
};

Map.defaultProps = {
  bearing: undefined,
  center: undefined,
  children: undefined,
  initialBounds: undefined,
  mapClassName: undefined,
  onAutomaticMoveEnd: undefined,
  onClick: undefined,
  onManualMoveEnd: undefined,
  onManualZoomEnd: undefined,
  onStyleLoad: undefined,
};

export default React.memo(Map);
